import {
  ParsedMermaidDiagram,
  ParsedMermaidNode,
  ParsedMermaidEdge,
  ParsedMermaidMetadata,
  MermaidDiagramType,
  MermaidNodeShape,
  MermaidEdgeStyle,
  MermaidParseError,
} from './types';

/**
 * Mermaid Parser - Lightweight custom parser for Mermaid diagram syntax
 * Supports flowcharts, sequence diagrams, class diagrams, state diagrams, and ER diagrams
 */
export class MermaidParser {
  /**
   * Parse a Mermaid diagram specification
   */
  static parse(spec: string): ParsedMermaidDiagram {
    if (!spec || typeof spec !== 'string') {
      throw new Error('Invalid Mermaid specification: must be a non-empty string');
    }

    const trimmed = spec.trim();
    if (!trimmed) {
      throw new Error('Invalid Mermaid specification: empty string');
    }

    // Detect diagram type
    const metadata = this.detectDiagramType(trimmed);
    const lines = trimmed.split(/\r?\n/).map(l => l.trim()).filter(Boolean);

    // Remove diagram type declaration line
    if (lines.length > 0 && this.isDiagramTypeLine(lines[0])) {
      lines.shift();
    }

    // Parse based on diagram type
    switch (metadata.type) {
      case 'flowchart':
      case 'graph':
        return this.parseFlowchart(lines, metadata);
      case 'sequenceDiagram':
        return this.parseSequenceDiagram(lines, metadata);
      case 'classDiagram':
        return this.parseClassDiagram(lines, metadata);
      case 'stateDiagram-v2':
        return this.parseStateDiagram(lines, metadata);
      case 'erDiagram':
        return this.parseERDiagram(lines, metadata);
      default:
        throw new Error(`Unsupported diagram type: ${metadata.type}`);
    }
  }

  /**
   * Detect diagram type from specification
   */
  private static detectDiagramType(spec: string): ParsedMermaidMetadata {
    const firstLine = spec.split(/\r?\n/)[0].trim().toLowerCase();

    if (firstLine.startsWith('flowchart')) {
      const directionMatch = firstLine.match(/flowchart\s+(TD|TB|BT|LR|RL|TB)/i);
      return {
        type: 'flowchart',
        direction: (directionMatch?.[1]?.toUpperCase() as any) || 'TD',
      };
    }

    if (firstLine.startsWith('graph')) {
      const directionMatch = firstLine.match(/graph\s+(TD|TB|BT|LR|RL)/i);
      return {
        type: 'graph',
        direction: (directionMatch?.[1]?.toUpperCase() as any) || 'TD',
      };
    }

    if (firstLine.startsWith('sequencediagram')) {
      return { type: 'sequenceDiagram' };
    }

    if (firstLine.startsWith('classdiagram')) {
      return { type: 'classDiagram' };
    }

    if (firstLine.startsWith('statediagram-v2') || firstLine.startsWith('stateDiagram-v2')) {
      return { type: 'stateDiagram-v2' };
    }

    if (firstLine.startsWith('erdiagram')) {
      return { type: 'erDiagram' };
    }

    // Default to flowchart if no type detected
    return { type: 'flowchart', direction: 'TD' };
  }

  /**
   * Check if a line is a diagram type declaration
   */
  private static isDiagramTypeLine(line: string): boolean {
    const lower = line.toLowerCase();
    return (
      lower.startsWith('flowchart') ||
      lower.startsWith('graph') ||
      lower.startsWith('sequencediagram') ||
      lower.startsWith('classdiagram') ||
      lower.startsWith('statediagram') ||
      lower.startsWith('erdiagram')
    );
  }

  /**
   * Parse flowchart/graph diagram
   */
  private static parseFlowchart(
    lines: string[],
    metadata: ParsedMermaidMetadata
  ): ParsedMermaidDiagram {
    const nodes = new Map<string, ParsedMermaidNode>();
    const edges: ParsedMermaidEdge[] = [];

    for (const line of lines) {
      // Skip comments and empty lines
      if (!line || line.startsWith('%%') || line.startsWith('//')) {
        continue;
      }

      // Parse edge: A --> B, A --- B, A -.-> B, A -->|label| B, A --> B : label
      const edgeMatch = line.match(
        /^([A-Za-z0-9_]+)\s*([-.]*[-.>]+)\s*([A-Za-z0-9_]+)(?:\s*\|\s*(.+?)\s*\|)?(?:\s*:\s*(.+))?/
      );
      if (edgeMatch) {
        const [, sourceId, arrow, targetId, midLabel, endLabel] = edgeMatch;
        const label = endLabel || midLabel || undefined;

        // Ensure nodes exist
        if (!nodes.has(sourceId)) {
          nodes.set(sourceId, { id: sourceId, label: sourceId });
        }
        if (!nodes.has(targetId)) {
          nodes.set(targetId, { id: targetId, label: targetId });
        }

        // Determine edge style
        let style: MermaidEdgeStyle = 'solid';
        if (arrow.includes('.')) {
          style = arrow.includes('-') ? 'dashed' : 'dotted';
        }

        edges.push({
          sourceId,
          targetId,
          label,
          style,
        });
        continue;
      }

      // Parse node definition: A[Label], B((Node)), C{Decision}, D[Label] style="..."
      const nodeMatch = line.match(
        /^([A-Za-z0-9_]+)\s*(\[([^\]]+)\]|\(\(([^\)]+)\)\)|\(([^\)]+)\)|\{([^}]+)\}|\[\[([^\]]+)\]\]|\[\(([^\)]+)\)\]|\[\{([^}]+)\}\]|\[\/([^\/]+)\/\]|\[\\\\([^\\\\]+)\\\\]|\[\\\\([^\\\\]+)\/\]|\[\/([^\/]+)\\\\])/
      );
      if (nodeMatch) {
        const id = nodeMatch[1];
        const label =
          nodeMatch[3] || // [Label]
          nodeMatch[4] || // ((Label))
          nodeMatch[5] || // (Label)
          nodeMatch[6] || // {Label}
          nodeMatch[7] || // [[Label]]
          nodeMatch[8] || // [(Label)]
          nodeMatch[9] || // [{Label}]
          nodeMatch[10] || // [/Label/]
          nodeMatch[11] || // [\\Label\\]
          nodeMatch[12] || // [\\Label/]
          nodeMatch[13] || // [/Label\\]
          id;

        // Determine shape
        let shape: MermaidNodeShape = 'rectangle';
        if (nodeMatch[2].startsWith('((')) shape = 'circle';
        else if (nodeMatch[2].startsWith('{')) shape = 'diamond';
        else if (nodeMatch[2].startsWith('((')) shape = 'circle';
        else if (nodeMatch[2].startsWith('(')) shape = 'rounded';
        else if (nodeMatch[2].startsWith('[[')) shape = 'subroutine';
        else if (nodeMatch[2].includes('/')) shape = 'parallelogram';
        else if (nodeMatch[2].includes('\\')) shape = 'parallelogram-alt';

        // Parse style if present
        const styleMatch = line.match(/style\s+([A-Za-z0-9_]+)\s+(.+)/);
        const style = styleMatch?.[2];

        // Parse class if present
        const classMatch = line.match(/classDef\s+([A-Za-z0-9_]+)/);
        const className = classMatch?.[1];

        nodes.set(id, {
          id,
          label: label.trim(),
          shape,
          style,
          class: className,
        });
        continue;
      }

      // Parse classDef: classDef className fill:#color,stroke:#color
      const classDefMatch = line.match(/classDef\s+([A-Za-z0-9_]+)\s+(.+)/);
      if (classDefMatch) {
        // Store class definition for later use
        // This would be used when applying classes to nodes
        continue;
      }

      // Parse class application: class A,B,C className
      const classApplyMatch = line.match(/class\s+([A-Za-z0-9_,\s]+)\s+([A-Za-z0-9_]+)/);
      if (classApplyMatch) {
        const nodeIds = classApplyMatch[1].split(',').map(id => id.trim());
        const className = classApplyMatch[2];
        nodeIds.forEach(nodeId => {
          if (nodes.has(nodeId)) {
            nodes.get(nodeId)!.class = className;
          }
        });
        continue;
      }
    }

    return {
      metadata,
      nodes: Array.from(nodes.values()),
      edges,
    };
  }

  /**
   * Parse sequence diagram
   */
  private static parseSequenceDiagram(
    lines: string[],
    metadata: ParsedMermaidMetadata
  ): ParsedMermaidDiagram {
    const nodes = new Map<string, ParsedMermaidNode>();
    const edges: ParsedMermaidEdge[] = [];

    for (const line of lines) {
      if (!line || line.startsWith('%%') || line.startsWith('//')) {
        continue;
      }

      // Parse participant: participant A as Alias, actor B
      const participantMatch = line.match(
        /^(participant|actor|participant|autonumber)\s+([A-Za-z0-9_]+)(?:\s+as\s+(.+))?/i
      );
      if (participantMatch) {
        const [, type, id, alias] = participantMatch;
        const label = alias?.trim() || id;
        nodes.set(id, {
          id,
          label,
          type: type.toLowerCase(),
          shape: 'rectangle',
        });
        continue;
      }

      // Parse message: A->>B: Message, A-->>B: Async, A->B: Sync
      const messageMatch = line.match(
        /^([A-Za-z0-9_]+)\s*([-.]*[-.>]+)\s*([A-Za-z0-9_]+)\s*:\s*(.+)/i
      );
      if (messageMatch) {
        const [, sourceId, arrow, targetId, label] = messageMatch;

        // Ensure participants exist
        if (!nodes.has(sourceId)) {
          nodes.set(sourceId, { id: sourceId, label: sourceId, type: 'participant' });
        }
        if (!nodes.has(targetId)) {
          nodes.set(targetId, { id: targetId, label: targetId, type: 'participant' });
        }

        let style: MermaidEdgeStyle = 'solid';
        if (arrow.includes('.')) {
          style = 'dotted';
        } else if (arrow.includes('--')) {
          style = 'dashed';
        }

        edges.push({
          sourceId,
          targetId,
          label: label.trim(),
          style,
          type: arrow.includes('>>') ? 'async' : 'sync',
        });
        continue;
      }
    }

    return {
      metadata,
      nodes: Array.from(nodes.values()),
      edges,
    };
  }

  /**
   * Parse class diagram
   */
  private static parseClassDiagram(
    lines: string[],
    metadata: ParsedMermaidMetadata
  ): ParsedMermaidDiagram {
    const nodes = new Map<string, ParsedMermaidNode>();
    const edges: ParsedMermaidEdge[] = [];

    for (const line of lines) {
      if (!line || line.startsWith('%%') || line.startsWith('//')) {
        continue;
      }

      // Parse class definition: class ClassName { +method() }, class A
      const classMatch = line.match(/^class\s+([A-Za-z0-9_]+)(?:\s*\{([^}]*)\})?/i);
      if (classMatch) {
        const [, id, body] = classMatch;
        const label = body ? `${id}\n${body.trim()}` : id;
        nodes.set(id, {
          id,
          label,
          shape: 'rectangle',
          type: 'class',
        });
        continue;
      }

      // Parse relationship: A <|-- B, A --> B, A <-- B, A <--> B
      const relationMatch = line.match(
        /^([A-Za-z0-9_]+)\s*([<|]*[-.>]+[|]*)\s*([A-Za-z0-9_]+)(?:\s*:\s*(.+))?/i
      );
      if (relationMatch) {
        const [, sourceId, arrow, targetId, label] = relationMatch;

        // Ensure classes exist
        if (!nodes.has(sourceId)) {
          nodes.set(sourceId, { id: sourceId, label: sourceId, type: 'class' });
        }
        if (!nodes.has(targetId)) {
          nodes.set(targetId, { id: targetId, label: targetId, type: 'class' });
        }

        // Determine relationship type
        let type = 'association';
        if (arrow.includes('|--')) type = 'inheritance';
        else if (arrow.includes('--')) type = 'association';
        else if (arrow.includes('..')) type = 'dependency';

        edges.push({
          sourceId,
          targetId,
          label: label?.trim(),
          type,
          style: arrow.includes('..') ? 'dashed' : 'solid',
        });
        continue;
      }
    }

    return {
      metadata,
      nodes: Array.from(nodes.values()),
      edges,
    };
  }

  /**
   * Parse state diagram
   */
  private static parseStateDiagram(
    lines: string[],
    metadata: ParsedMermaidMetadata
  ): ParsedMermaidDiagram {
    const nodes = new Map<string, ParsedMermaidNode>();
    const edges: ParsedMermaidEdge[] = [];

    for (const line of lines) {
      if (!line || line.startsWith('%%') || line.startsWith('//')) {
        continue;
      }

      // Parse state: State1, State2 : Entry, [*]
      const stateMatch = line.match(/^([A-Za-z0-9_*]+)(?:\s*:\s*(.+))?/i);
      if (stateMatch) {
        const [, id, label] = stateMatch;
        const stateLabel = label || (id === '[*]' ? 'Start/End' : id);
        nodes.set(id, {
          id,
          label: stateLabel,
          shape: id === '[*]' ? 'circle' : 'rounded',
          type: 'state',
        });
        continue;
      }

      // Parse transition: State1 --> State2 : event
      const transitionMatch = line.match(
        /^([A-Za-z0-9_*]+)\s*[-.>]+\s*([A-Za-z0-9_*]+)(?:\s*:\s*(.+))?/i
      );
      if (transitionMatch) {
        const [, sourceId, targetId, label] = transitionMatch;

        // Ensure states exist
        if (!nodes.has(sourceId)) {
          nodes.set(sourceId, {
            id: sourceId,
            label: sourceId === '[*]' ? 'Start/End' : sourceId,
            type: 'state',
          });
        }
        if (!nodes.has(targetId)) {
          nodes.set(targetId, {
            id: targetId,
            label: targetId === '[*]' ? 'Start/End' : targetId,
            type: 'state',
          });
        }

        edges.push({
          sourceId,
          targetId,
          label: label?.trim(),
          type: 'transition',
          style: 'solid',
        });
        continue;
      }
    }

    return {
      metadata,
      nodes: Array.from(nodes.values()),
      edges,
    };
  }

  /**
   * Parse ER diagram
   */
  private static parseERDiagram(
    lines: string[],
    metadata: ParsedMermaidMetadata
  ): ParsedMermaidDiagram {
    const nodes = new Map<string, ParsedMermaidNode>();
    const edges: ParsedMermaidEdge[] = [];

    for (const line of lines) {
      if (!line || line.startsWith('%%') || line.startsWith('//')) {
        continue;
      }

      // Parse entity: ENTITY { type field }
      const entityMatch = line.match(/^([A-Za-z0-9_]+)\s*\{([^}]+)\}/i);
      if (entityMatch) {
        const [, id, body] = entityMatch;
        const label = `${id}\n${body.trim()}`;
        nodes.set(id, {
          id,
          label,
          shape: 'rectangle',
          type: 'entity',
        });
        continue;
      }

      // Parse relationship: ENTITY1 ||--o{ ENTITY2 : relationship
      const relationMatch = line.match(
        /^([A-Za-z0-9_]+)\s*([|o{}]+)\s*([A-Za-z0-9_]+)(?:\s*:\s*(.+))?/i
      );
      if (relationMatch) {
        const [, sourceId, cardinality, targetId, label] = relationMatch;

        // Ensure entities exist
        if (!nodes.has(sourceId)) {
          nodes.set(sourceId, { id: sourceId, label: sourceId, type: 'entity' });
        }
        if (!nodes.has(targetId)) {
          nodes.set(targetId, { id: targetId, label: targetId, type: 'entity' });
        }

        edges.push({
          sourceId,
          targetId,
          label: label?.trim() || cardinality,
          type: 'relationship',
          style: 'solid',
        });
        continue;
      }
    }

    return {
      metadata,
      nodes: Array.from(nodes.values()),
      edges,
    };
  }

  /**
   * Validate parsed diagram
   */
  static validate(diagram: ParsedMermaidDiagram): MermaidParseError[] {
    const errors: MermaidParseError[] = [];

    if (!diagram.metadata) {
      errors.push({ message: 'Missing diagram metadata' });
    }

    if (!diagram.nodes || diagram.nodes.length === 0) {
      errors.push({ message: 'No nodes found in diagram' });
    }

    // Validate edges reference existing nodes
    for (const edge of diagram.edges) {
      const sourceExists = diagram.nodes.some(n => n.id === edge.sourceId);
      const targetExists = diagram.nodes.some(n => n.id === edge.targetId);

      if (!sourceExists) {
        errors.push({
          message: `Edge references unknown source node: ${edge.sourceId}`,
          diagramType: diagram.metadata.type,
        });
      }

      if (!targetExists) {
        errors.push({
          message: `Edge references unknown target node: ${edge.targetId}`,
          diagramType: diagram.metadata.type,
        });
      }
    }

    return errors;
  }
}



